import React from 'react';
import { Truck, Zap, Globe, Package, ArrowRight, MessageCircle, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header3';
import Footer from './components/Footer3';

const ShippingAndExchange3 = () => {
    const navigate = useNavigate();

    const shippingOptions = [
        { icon: Truck, title: 'Standard Shipping', time: '5-7 Business Days', cost: 'Free on orders over \u20B92,999', description: 'Reliable delivery across India via our trusted logistics partners. Orders under \u20B92,999 are charged a flat \u20B9149 shipping fee.' },
        { icon: Zap, title: 'Express Shipping', time: '2-3 Business Days', cost: '\u20B9499', description: 'Priority dispatch and expedited delivery for when you need your order sooner. Available for most metro cities and tier-1 locations.' },
        { icon: Globe, title: 'International Shipping', time: '7-14 Business Days', cost: 'Varies by country', description: 'We ship to select countries worldwide. Shipping costs are calculated at checkout based on destination and package weight. Customs duties may apply.' },
    ];

    const exchangeSteps = [
        { step: 1, title: 'Request', description: 'Initiate an exchange request within 7 days of delivery through your account dashboard or by contacting our support team with your order number.' },
        { step: 2, title: 'Approval', description: 'Our team will review your request within 24-48 hours and send you a confirmation email with return shipping instructions and a prepaid label.' },
        { step: 3, title: 'Ship Back', description: 'Pack the item securely in its original packaging with all tags attached. Drop it off at the nearest courier partner location or schedule a pickup.' },
        { step: 4, title: 'Receive', description: 'Once we receive and inspect the returned item, your exchange will be processed within 3-5 business days. Refunds are credited within 5-7 business days.' },
    ];

    const policies = [
        { title: 'Eligible for Return', items: ['Unworn and unwashed items', 'Items with all original tags attached', 'Items in original packaging', 'Items returned within 7 days of delivery'] },
        { title: 'Not Eligible for Return', items: ['Custom-made or altered garments', 'Intimates and undergarments', 'Items without tags or packaging', 'Items returned after 7 days'] },
    ];

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Lato:wght@300;400;700&display=swap');`}</style>
            <div style={{ fontFamily: "'Lato', sans-serif" }} className="min-h-screen bg-[#FFFDF7]">
                <Header />

                <main className="pt-24 pb-16">
                    {/* Hero */}
                    <section className="py-10 lg:py-16 px-4 text-center" style={{ borderBottom: '1px solid #E8E0D8' }}>
                        <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(114,47,55,0.05)', border: '1px solid rgba(114,47,55,0.1)' }}>
                            <Package size={24} style={{ color: '#722F37' }} />
                        </div>
                        <h1 style={{ fontFamily: "'Playfair Display', serif" }} className="text-3xl lg:text-5xl font-bold text-[#722F37] mb-3">Shipping & Returns</h1>
                        <p className="text-sm" style={{ color: '#6B7280' }}>Everything you need to know about delivery, returns, and exchanges.</p>
                    </section>

                    <div className="max-w-[1000px] mx-auto px-4">
                        {/* Shipping Options */}
                        <section className="py-12 lg:py-16">
                            <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-2xl lg:text-3xl font-bold text-[#722F37] mb-8 text-center">Shipping Options</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {shippingOptions.map((opt, idx) => (
                                    <div key={idx} className="p-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E0D8' }}>
                                        <div className="w-12 h-12 mb-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(114,47,55,0.05)', border: '1px solid rgba(114,47,55,0.1)' }}>
                                            <opt.icon size={22} style={{ color: '#722F37' }} />
                                        </div>
                                        <h3 className="text-base font-bold mb-1" style={{ color: '#1A1A1A' }}>{opt.title}</h3>
                                        <p className="text-sm font-bold mb-1" style={{ color: '#B8860B' }}>{opt.time}</p>
                                        <p className="text-xs font-bold mb-3" style={{ color: '#722F37' }}>{opt.cost}</p>
                                        <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{opt.description}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Exchange Process */}
                        <section className="py-12 lg:py-16" style={{ borderTop: '1px solid #E8E0D8' }}>
                            <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-2xl lg:text-3xl font-bold text-[#722F37] mb-8 text-center">Exchange Process</h2>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                {exchangeSteps.map((s, idx) => (
                                    <div key={idx} className="text-center p-6" style={{ backgroundColor: '#FFF9F0', border: '1px solid #E8E0D8' }}>
                                        <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center text-sm font-bold" style={{ backgroundColor: '#722F37', color: '#FFFFFF' }}>
                                            {s.step}
                                        </div>
                                        <h3 className="text-sm font-bold mb-2" style={{ color: '#722F37' }}>{s.title}</h3>
                                        <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>{s.description}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Return Policy */}
                        <section className="py-12 lg:py-16" style={{ borderTop: '1px solid #E8E0D8' }}>
                            <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-2xl lg:text-3xl font-bold text-[#722F37] mb-8 text-center">Return Policy</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {policies.map((policy, idx) => (
                                    <div key={idx} className="p-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E0D8' }}>
                                        <h3 className="text-base font-bold mb-4" style={{ color: idx === 0 ? '#2ECC71' : '#722F37' }}>{policy.title}</h3>
                                        <ul className="space-y-2">
                                            {policy.items.map((item, iIdx) => (
                                                <li key={iIdx} className="flex items-start gap-2 text-sm" style={{ color: '#6B7280' }}>
                                                    <span style={{ color: idx === 0 ? '#2ECC71' : '#722F37', marginTop: '4px' }}>{idx === 0 ? '\u2713' : '\u2717'}</span>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Contact CTA */}
                        <section className="py-12 text-center" style={{ borderTop: '1px solid #E8E0D8' }}>
                            <h3 style={{ fontFamily: "'Playfair Display', serif" }} className="text-xl font-bold text-[#722F37] mb-2">Need Help?</h3>
                            <p className="text-sm mb-6" style={{ color: '#6B7280' }}>Our team is available Mon-Sat, 10AM-7PM IST</p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <button onClick={() => navigate('/template3/contact')} className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-wider uppercase" style={{ backgroundColor: '#722F37', color: '#FFFFFF' }}>
                                    <MessageCircle size={16} /> Contact Us
                                </button>
                                <div className="inline-flex items-center gap-2 px-6 py-3 text-sm" style={{ border: '1px solid #E8E0D8', color: '#6B7280' }}>
                                    <Mail size={16} style={{ color: '#B8860B' }} /> support@vastra.com
                                </div>
                            </div>
                        </section>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default ShippingAndExchange3;
